import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { AppExceptionModel } from '../../models/app-exception.model';

import { Session } from '../../models/session.school.model';
import * as sessionActions from '../actions/session.school.actions';

export interface SessionState extends EntityState<Session> {
  loading: boolean;
  loaded: boolean;
  inProgress: boolean;
  sessionStatsLoading: boolean;
  error: AppExceptionModel | undefined;
}

export const SessionAdapter: EntityAdapter<Session> =
  createEntityAdapter<Session>();

export const initialState: SessionState = SessionAdapter.getInitialState({
  loading: false,
  loaded: false,
  inProgress: false,
  sessionStatsLoading: false,
  error: undefined,
});

export const schoolSessionReducer = createReducer(
  initialState,
  on(sessionActions.getSessions, (state, {}) => ({
    ...state,
    loading: true,
    loaded: false,
    error: undefined,
  })),
  on(sessionActions.getSession, (state, {}) => ({
    ...state,
    error: undefined,
  })),
  on(sessionActions.addSessionToStore, (state, { session }) =>
    SessionAdapter.upsertOne(session, {
      ...state,
      error: undefined,
      loading: false,
      loaded: true,
    }),
  ),
  on(sessionActions.addSessionsToStore, (state, { sessions }) => {
    return SessionAdapter.upsertMany(sessions, {
      ...state,
      loading: false,
      loaded: true,
      inProgress: false,
      error: undefined,
    });
  }),
  on(sessionActions.getSessionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    loaded: true,
    error: error,
  })),
  on(sessionActions.create, (state, { session }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(sessionActions.createSuccess, (state, { session }) =>
    SessionAdapter.upsertOne(session, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(sessionActions.createFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(sessionActions.update, (state, { session }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(sessionActions.updateSuccess, (state, { session }) =>
    SessionAdapter.upsertOne(session, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(sessionActions.updateFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(sessionActions.updateStatus, (state, { session }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(sessionActions.updateStatusSuccess, (state, { session }) =>
    SessionAdapter.upsertOne(session, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(sessionActions.updateStatusFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(sessionActions.deleteAction, (state, { id }) => ({
    ...state,
    error: undefined,
    inProgress: true,
  })),
  on(sessionActions.deleteSuccess, (state, { id }) =>
    SessionAdapter.removeOne(id, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(sessionActions.deleteFailure, (state, { error }) => ({
    ...state,
    error: error,
    inProgress: false,
  })),
  on(sessionActions.getSessionStats, (state) => ({
    ...state,
    sessionStatsLoading: true,
  })),
  on(sessionActions.getSessionStatsDone, (state) => ({
    ...state,
    sessionStatsLoading: false,
  })),
  on(sessionActions.getSessionStatsFailure, (state) => ({
    ...state,
    sessionStatsLoading: false,
  })),
  on(sessionActions.clearFromStore, (state, { ids }) =>
    SessionAdapter.removeMany(ids, {
      ...state,
      error: undefined,
      inProgress: false,
    }),
  ),
  on(sessionActions.clear, (state) => initialState),
);
